/**
 * /auth/smoke — RBAC verification page (Phase 2 #5B.4).
 *
 * Shows the current session + lets you fire smoke tests against the things
 * RBAC should gate: FastAPI role-checked routes (/profile/me, /admin/probe)
 * and S3 PutObject against the per-identity inbox prefix policy.
 *
 * Expected outcomes per role (after the Auth0 Action + CDK
 * enableAuth0RoleMappings=true land):
 *
 *   any auth user      GET /profile/me           -> 200
 *   client             GET /admin/probe          -> 403
 *   super/brokerage    GET /admin/probe          -> 200
 *   any auth user      S3 PutObject correct      -> 200 (inbox/<identityId>/*)
 *   any auth user      S3 PutObject wrong prefix -> 403
 *
 * The page is dev-only by intent; gating to a feature flag or removing it
 * before Phase 3 hand-off is a followup.
 */

import { PutObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { useState } from "react";
import { Link } from "react-router";

import { loadCognitoCreds, loadSession } from "./session";

// Each service runs as its own FastAPI app on its own port in local dev. In
// prod they're behind API Gateway on a single host, but locally the smoke
// has to know which port hosts which prefix. This map is dev-only.
const SERVICE_PORTS: Record<string, number> = {
  identity: 8000,
  profile: 8001,
  admin: 8002,
};

function urlFor(path: string): string {
  const segment = path.split("/")[1] ?? "";
  const port = SERVICE_PORTS[segment] ?? 8000;
  return `http://localhost:${port}${path}`;
}

const DOCS_BUCKET = import.meta.env.VITE_DOCS_BUCKET as string | undefined;
const REGION =
  (import.meta.env.VITE_AWS_REGION as string | undefined) ?? "us-east-1";

type ProbeResult = {
  label: string;
  ok: boolean;
  status: number | string;
  body?: string;
};

export function Smoke() {
  const session = loadSession();
  const cognito = loadCognitoCreds();
  const [results, setResults] = useState<ProbeResult[]>([]);

  function append(result: ProbeResult): void {
    setResults((prev) => [...prev, result]);
  }

  async function hitApi(label: string, path: string): Promise<void> {
    if (!session) {
      append({ label, ok: false, status: "no session" });
      return;
    }
    try {
      const resp = await fetch(urlFor(path), {
        headers: { Authorization: `Bearer ${session.accessToken}` },
      });
      const body = await resp.text();
      append({ label, ok: resp.ok, status: resp.status, body });
    } catch (err) {
      append({
        label,
        ok: false,
        status: "fetch error",
        body: err instanceof Error ? err.message : String(err),
      });
    }
  }

  async function s3Put(label: string, key: string): Promise<void> {
    if (!cognito || !DOCS_BUCKET) {
      append({
        label,
        ok: false,
        status: !cognito ? "no cognito creds" : "no VITE_DOCS_BUCKET",
      });
      return;
    }
    const client = new S3Client({
      region: REGION,
      credentials: {
        accessKeyId: cognito.accessKeyId,
        secretAccessKey: cognito.secretAccessKey,
        sessionToken: cognito.sessionToken,
      },
    });
    try {
      await client.send(
        new PutObjectCommand({
          Bucket: DOCS_BUCKET,
          Key: key,
          Body: `smoke-${Date.now()}`,
        }),
      );
      append({ label, ok: true, status: 200, body: `wrote ${key}` });
    } catch (err) {
      // AccessDenied is a 403 wrapped in an SDK exception; surface the
      // distinctive token so the smoke is easy to read.
      const msg = err instanceof Error ? err.message : String(err);
      const denied = /AccessDenied|Access Denied|403/i.test(msg);
      append({ label, ok: false, status: denied ? 403 : "error", body: msg });
    }
  }

  return (
    <main style={{ maxWidth: 800, margin: "40px auto", padding: 24 }}>
      <h1>RBAC smoke</h1>
      <p>
        <Link to="/login">Back to login</Link> · <Link to="/">Home</Link>
      </p>

      <section style={{ marginTop: 16 }}>
        <h2>Session</h2>
        <pre style={{ background: "#f5f5f5", padding: 12, overflow: "auto" }}>
          {session
            ? JSON.stringify(
                {
                  userId: session.userId,
                  tenantId: session.tenantId,
                  primaryRole: session.primaryRole,
                  expiresAt: new Date(session.expiresAt * 1000).toISOString(),
                },
                null,
                2,
              )
            : "(no session — go log in)"}
        </pre>
        <h3>Cognito creds (only set if you signed in via Auth0)</h3>
        <pre style={{ background: "#f5f5f5", padding: 12, overflow: "auto" }}>
          {cognito
            ? JSON.stringify(
                {
                  identityId: cognito.identityId,
                  accessKeyId: cognito.accessKeyId.slice(0, 8) + "…",
                  expiresAt: new Date(cognito.expiresAt * 1000).toISOString(),
                },
                null,
                2,
              )
            : "(no cognito creds — sign in via 'Continue with Auth0' on /login)"}
        </pre>
      </section>

      <section style={{ marginTop: 16 }}>
        <h2>API smokes</h2>
        <button onClick={() => void hitApi("GET /profile/me", "/profile/me")}>
          GET /profile/me
        </button>{" "}
        <button onClick={() => void hitApi("GET /admin/probe", "/admin/probe")}>
          GET /admin/probe
        </button>
      </section>

      <section style={{ marginTop: 16 }}>
        <h2>S3 PutObject smokes</h2>
        <button
          onClick={() =>
            void s3Put(
              "S3 PutObject (correct prefix)",
              `inbox/${cognito?.identityId ?? "no-identity"}/smoke.txt`,
            )
          }
        >
          PutObject inbox/&lt;identityId&gt;/smoke.txt (expect 200)
        </button>{" "}
        <button
          onClick={() =>
            void s3Put(
              "S3 PutObject (wrong prefix)",
              `inbox/00000000-0000-0000-0000-000000000000/smoke.txt`,
            )
          }
        >
          PutObject inbox/00000000…/smoke.txt (expect 403)
        </button>
      </section>

      <section style={{ marginTop: 24 }}>
        <h2>Results</h2>
        {results.length === 0 ? (
          <p>(none yet — click a smoke above)</p>
        ) : (
          <ol>
            {results.map((r, i) => (
              <li key={i} style={{ marginBottom: 8 }}>
                <strong style={{ color: r.ok ? "#0a0" : "#a00" }}>
                  {r.label}
                </strong>{" "}
                — {String(r.status)}
                {r.body && (
                  <pre
                    style={{
                      background: "#f5f5f5",
                      padding: 8,
                      marginTop: 4,
                      whiteSpace: "pre-wrap",
                      maxHeight: 200,
                      overflow: "auto",
                    }}
                  >
                    {r.body}
                  </pre>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>
    </main>
  );
}
